import { skipToken, useQuery } from '@tanstack/react-query';
import { Loader2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { businessQueryKey, getBusiness } from '@/lib/api/businesses';
import { listTiers, TIERS_QUERY_KEY } from '@/lib/api/tiers';

import { PlanPicker } from './plan-picker';
import { SubscriptionStatusCard } from './subscription-status-card';

export function Billing() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;

  const businessQuery = useQuery({
    queryKey: businessQueryKey(businessId ?? ''),
    queryFn: businessId ? () => getBusiness(businessId) : skipToken,
  });

  const tiersQuery = useQuery({ queryKey: TIERS_QUERY_KEY, queryFn: listTiers });

  const isPending = businessQuery.isPending || tiersQuery.isPending;
  const tierLabel = tiersQuery.data?.find(
    (tier) => tier.id === businessQuery.data?.tier_id,
  )?.label;

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold">{t.billingTitle}</h1>
        <p className="text-sm text-muted-foreground">{t.billingSubtitle}</p>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2Icon className="w-5 h-5 animate-spin" />
        </div>
      ) : businessQuery.isError ? (
        <p className="text-sm text-destructive">{t.billingCheckoutError}</p>
      ) : (
        businessQuery.data && (
          <>
            <SubscriptionStatusCard business={businessQuery.data} tierLabel={tierLabel} />
            <PlanPicker businessId={businessQuery.data.id} currentTierId={businessQuery.data.tier_id} />
          </>
        )
      )}
    </div>
  );
}
